import React, { useEffect, useState } from 'react';
import PageContainer from '../../../components/PageContainer';
import {
    Box,
    Button,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    TextField,
} from '@mui/material';
import * as Yup from 'yup';
import { AccountTreeOutlined, Add, DeleteOutline, PeopleOutlined } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { getUsersInRole, addRole, getAllPermissions, getRoles, getRolesAndPermissions, matchPermissionsToRole, removeRole, removeUserRole } from '../../../redux/api/userMgt';
import { Form, Formik } from 'formik';
import TextInput from '../../../components/common/TextInput';
import { useHistory } from 'react-router-dom';

const UserRoles = () => {
    const defRole = { roleName: '' };
    const history = useHistory();
    const [fetching, setFetching] = useState(false);

    const [processing, setProcessing] = useState(false);
    const [mapping, setMapping] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [openRoleUsersDialog, setOpenRoleUsersDialog] = useState(false);
    const [actionModal, setActionModal] = useState(null);
    const [selectedRole, setSelectedRole] = useState(defRole);
    const { enqueueSnackbar } = useSnackbar();
    const [records, setRecords] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [permissions, setPermissions] = useState([]);

    const columns = [
        {
            field: 'sn',
            headerName: 'S/N',
            width: 100,
            sortable: false,
            renderCell: (params) => {
                return <p style={{ margin: 'auto' }}>{(params?.api?.getRowIndex(params?.row?.roleId) || 0) + 1}</p>;
            },
        },
        {
            field: 'action',
            headerName: 'Quick Action',
            width: 200,
            sortable: false,
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <IconButton
                            size="small"
                            color={'primary'}
                            onClick={() => {
                                setSelectedRole(params?.row || null);
                                setOpenDialog(true);
                            }}
                            variant="outlined"
                            title={'Map permissions to role'}
                        >
                            <AccountTreeOutlined />
                        </IconButton>

                        <IconButton
                            size="small"
                            className="ml-05 mr-05"
                            color={'info'}
                            onClick={async () => {
                                try {
                                    setFetching(params?.row?.roleId);
                                    const roleUsers = await getUsersInRole(params?.row?.roleId);
                                    setSelectedRole({
                                        ...params?.row,
                                        roleUsers,
                                    });
                                    setOpenRoleUsersDialog(true);
                                } catch (err) {
                                    enqueueSnackbar(err?.message || 'An error occurred', { variant: 'error' });
                                } finally {
                                    setFetching(null);
                                }
                            }}
                            variant="outlined"
                            title={'View users in role'}
                            disabled={fetching}
                        >
                            {fetching === params?.row?.roleId ? <CircularProgress size={15} color="inherit" /> : <PeopleOutlined />}
                        </IconButton>

                        <IconButton
                            size="small"
                            color={'error'}
                            onClick={() => {
                                setSelectedRole(params?.row || null);
                                setActionModal('remove');
                            }}
                            variant="outlined"
                            title={'Remove role'}
                        >
                            <DeleteOutline />
                        </IconButton>
                    </div>
                );
            },
        },
        { field: 'roleName', headerName: 'Role', width: 300 },
        { field: 'roleDesc', headerName: 'Description', width: 350 },
        // { field: 'createdBy', headerName: 'Created By', width: 200 },
        // { field: 'createdDate', headerName: 'Creation Date', width: 200, valueGetter: (params) => (params?.row?.createdDate) ? (moment(params?.row?.createdDate).format('DD MMMM, YYYY')) : '' },
        // { field: 'createdTime', headerName: 'Creation Time', width: 200, valueGetter: (params) => (params?.row?.createdDate) ? (moment(params?.row?.createdDate).format('hh:mm:ss A')) : '' }
    ];

    const init = async () => {
        try {
            setProcessing(true);
            const res = await getRoles();
            const rolesPermissions = await getRolesAndPermissions();
            const permissionResponse = await getAllPermissions();
            const roles = res?.map(({ roleId, ...roles }) => {
                const permissions = rolesPermissions?.filter(({ roleVm }) => roleVm?.roleId == roleId)?.map(({ permissionVm }) => permissionVm);
                return {
                    ...roles,
                    roleId,
                    permissions,
                };
            });
            setRecords(roles || []);
            setPermissions(permissionResponse);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const remove = async (values) => {
        try {
            await removeRole(values);
            setActionModal(false);
            setSelectedRole(defRole);
            enqueueSnackbar('Role Removed Successfully');
            init();
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    const create = async (values) => {
        try {
            await addRole(values);
            setActionModal(false);
            setSelectedRole(defRole);
            enqueueSnackbar('Role Created Successfully');
            init();
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    const handleChange = ({ target }, value) => {
        let rolePermissions = [...selectedRole?.permissions] || [];
        if (value) {
            rolePermissions.push({
                id: parseInt(target.id),
                name: target.name,
            });
        } else {
            rolePermissions = rolePermissions.filter(({ id }) => id != parseInt(target.id));
        }
        setSelectedRole({ ...selectedRole, permissions: rolePermissions });
    };

    const hasPermission = (permissionId) => {
        return selectedRole?.permissions?.map((x) => String(x?.id))?.indexOf(String(permissionId)) >= 0 ? true : false;
    };

    const handleMapping = async () => {
        try {
            setMapping(true);
            const resp = await matchPermissionsToRole({
                roleId: selectedRole?.roleId,
                permissionIds: selectedRole?.permissions?.map((x) => parseInt(x?.id)),
            });
            init();
            setSelectedRole(null);
            setOpenDialog(false);
            enqueueSnackbar('Mapping successful');
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setMapping(false);
        }
    };

    const removeFromRole = async (userId) => {
        try {
            setFetching(userId);
            await removeUserRole({ userId, roleName: selectedRole?.roleName });
            setOpenRoleUsersDialog(false);
            enqueueSnackbar('Role Removed Successfully');
            init();
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setFetching(null);
        }
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <PageContainer
            pageTitle="CBN User Roles"
            tagline="Manage all CBN user roles"
            processing={processing}
            buttonComponent={
                <>
                    <Button onClick={() => setActionModal('create')} startIcon={<Add />} variant="contained">
                        Create Role
                    </Button>
                </>
            }
        >
            <Box>
                <div className="paper" style={{ height: 650, width: '100%', textAlign: 'center' }}>
                    <DataGrid
                        className={'center-first-column'}
                        rows={records}
                        getRowId={(row) => row.roleId}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10]}
                        checkboxSelection={false}
                        style={{ fontSize: '12px' }}
                        disableSelectionOnClick
                    />
                </div>
            </Box>

            <Dialog open={Boolean(actionModal)} maxWidth="xs" fullWidth>
                <Formik
                    initialValues={{ ...selectedRole }}
                    enableReinitialize
                    onSubmit={actionModal === 'create' ? create : remove}
                    validationSchema={Yup.object().shape(
                        actionModal === 'create'
                            ? {
                                  roleName: Yup.string().required('Role Name is required.'),
                              }
                            : {}
                    )}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <DialogContent>
                                {actionModal === 'create' ? (
                                    <>
                                        <h4 className="mb-1">Create User Role</h4>
                                        <TextInput id="roleName" label="Role Name" />
                                    </>
                                ) : (
                                    <>
                                        <h4 className="mb-1">Remove User Role</h4>
                                        <p>Kindly confirm deletion of role with role name: {selectedRole?.roleName}</p>
                                    </>
                                )}
                            </DialogContent>
                            <DialogActions className="pb-1 pr-1">
                                <Button variant="contained" color="secondary" onClick={() => setActionModal(null)} disabled={isSubmitting}>
                                    Cancel
                                </Button>
                                <Button variant="contained" color={actionModal === 'create' ? 'primary' : 'error'} type="submit" disabled={isSubmitting}>
                                    {Boolean(isSubmitting) ? <CircularProgress color="inherit" size={15} /> : actionModal === 'create' ? 'Create' : 'Remove'}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            <Dialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setSelectedRole(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle component={'div'} className="no-margin m-0 pt-2" style={{ textTransform: 'initial' }}>
                    <span className="m-0">
                        Map Permissions to Role: <b>{selectedRole?.roleName}</b>
                    </span>
                    <div className="custom-input mt-1">
                        <TextField inputProps={{ style: { fontSize: '12px' } }} size="small" fullWidth placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                    </div>
                </DialogTitle>
                <DialogContent
                    style={{
                        height: '500px',
                        width: '600px',
                        padding: '0.5rem 2rem 0 ',
                    }}
                >
                    <FormControl fullWidth component="fieldset" variant="standard">
                        <FormGroup>
                            {permissions
                                .filter((permission) => permission.name.toLowerCase().includes(searchText.toLowerCase()) || permission.description.toLowerCase().includes(searchText.toLowerCase()))
                                .map((permission, key) => (
                                    <>
                                        <FormControlLabel
                                            control={<Checkbox checked={hasPermission(permission.id)} id={permission.id} onChange={handleChange} name={permission.name} />}
                                            label={
                                                <p className="m-0">
                                                    {permission?.name || ''}
                                                    <br />
                                                    <small>{permission?.description || ''}</small>
                                                </p>
                                            }
                                            key={key}
                                        />
                                        <Divider className="mb-05" />
                                    </>
                                ))}
                        </FormGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions style={{ padding: '1rem' }}>
                    <Button
                        onClick={() => {
                            setOpenDialog(false);
                            setSelectedRole(null);
                        }}
                        color="secondary"
                        variant="contained"
                    >
                        Cancel
                    </Button>

                    <Button onClick={handleMapping} color="primary" variant="contained">
                        {mapping ? <CircularProgress size={15} color="inherit" /> : 'Save Mapping'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openRoleUsersDialog}
                onClose={() => {
                    setOpenRoleUsersDialog(false);
                    setSelectedRole(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className="no-margin m-0 pt-2" style={{ textTransform: 'initial' }}>
                    <span className="m-0">
                        All Users in Role: <b>{selectedRole?.roleName}</b>
                    </span>
                </DialogTitle>
                <DialogContent
                    style={{
                        height: '500px',
                        width: '600px',
                        padding: '0.5rem 2rem 0 ',
                    }}
                >
                    <List>
                        {selectedRole?.roleUsers?.map((user, key) => (
                            <ListItem divider className="pl-0 pr-0">
                                {/* <IconButton color="info" onClick={() => history.push(`${appRoutes.internal.users.default}/user/${user.id}`)}>
                                        <VisibilityOutlined />
                                    </IconButton> */}

                                <ListItemText className="ml-1">
                                    {user?.lastName} {user?.firstName}
                                </ListItemText>

                                <IconButton color="error" disabled={fetching == user?.id} onClick={() => removeFromRole(user?.id)}>
                                    {fetching == user?.id ? <CircularProgress color="inherit" size={15} /> : <DeleteOutline />}
                                </IconButton>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions style={{ padding: '1rem' }}>
                    <Button
                        onClick={() => {
                            setOpenRoleUsersDialog(false);
                            setSelectedRole(null);
                        }}
                        color="secondary"
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

export default UserRoles;
